<template>
  <div class="settings-section profile-settings">
    <h3 class="settings-section__label">Timezone</h3>
    <div class="timezone">
      <voxy-select
        class="timezone__select"
        :options="timeZones"
        :value="profile.timezone"
        size="l"
        :placeholder="timezoneSelectPlaceholder"
        @input="handleTimezoneInput"
        test-id="timezone-select"
      ></voxy-select>
    </div>
    <h3 class="settings-section__label">Email</h3>
    <p class="settings-section__value">{{ profile.email }}</p>

    <template v-if="canUseFeature(ConversationsFeatureConfigurationSlug)">
      <h3 class="settings-section__label">Chat Settings</h3>
      <div class="chat-settings">
        <voxy-checkbox id="chat-enabled-checkbox" v-model="hasChatEnabled" test-id="chat-enabled-checkbox">
          Enable async chat functionality
        </voxy-checkbox>
      </div>
    </template>

    <voxy-button id="logout-button" class="profile-settings__logout-button" @click="logout" view="primary" size="xl"
      >Logout</voxy-button
    >
  </div>
</template>

<script>
import { VoxyButton, VoxySelect, VoxyCheckbox } from '@voxy-team/vue-voxy';
import { mapActions, mapGetters } from 'vuex';
import debounce from 'lodash.debounce';
import { ConversationsFeatureConfigurationSlug } from '@/constants';

const DEBOUNCE_DELAY = 500;

export default {
  name: 'ProfileSettings',

  components: {
    VoxyButton,
    VoxySelect,
    VoxyCheckbox,
  },

  props: {
    profile: {
      type: Object,
      required: true,
    },
  },
  async created() {
    this.fetchTimeZones();
    this.debouncedUpdateChatEnabled = debounce(this.updateChatEnabled, DEBOUNCE_DELAY);

    // Fetch teacher profile if conversations feature is enabled
    if (this.canUseFeature(ConversationsFeatureConfigurationSlug)) {
      await this.fetchProfile();
    }
  },
  data() {
    return {
      selectedTimezone: this.profile.timezone,
      timezoneSelectPlaceholder: 'Search timezone',
      ConversationsFeatureConfigurationSlug,
      debouncedUpdateChatEnabled: null,
    };
  },
  computed: {
    ...mapGetters('timezones', ['timeZones']),
    ...mapGetters('features', ['canUseFeature']),
    ...mapGetters('manageTeachers', {
      teacherProfile: 'teacherProfile',
    }),

    hasChatEnabled: {
      get() {
        if (!this.teacherProfile) {
          return false;
        }

        return Boolean(this.teacherProfile.has_chat_enabled);
      },
      set(value) {
        this.debouncedUpdateChatEnabled(value);
      },
    },
  },
  methods: {
    ...mapActions('timezones', {
      fetchTimeZones: 'FETCH_TIME_ZONES',
    }),
    ...mapActions({
      updateUserTimezone: 'UPDATE_USER_TIMEZONE',
    }),
    ...mapActions('manageTeachers', {
      fetchTeacherProfile: 'FETCH_TEACHER_PROFILE',
      updateTeacherProfile: 'UPDATE_TEACHER_PROFILE',
    }),

    async fetchProfile() {
      const hashedEmail = this.profile.tutor_hashed_email;
      await this.fetchTeacherProfile(hashedEmail);
    },

    async updateChatEnabled(value) {
      try {
        const hashedEmail = this.profile.tutor_hashed_email;

        await this.updateTeacherProfile({
          teacherHashEmail: hashedEmail,
          value: {
            has_chat_enabled: value,
          },
        });
      } catch (error) {
        throw new Error(error);
      }
    },
    async handleTimezoneInput(newTimezone) {
      const timezoneWithoutSpaces = newTimezone.replace(/ /g, '_');
      await this.updateUserTimezone(timezoneWithoutSpaces);
    },
    async logout() {
      await this.$store.dispatch('LOGOUT');

      this.$router.push({ name: 'login' }).catch(error => {
        throw new Error(error);
      });
    },
  },
};
</script>

<style lang="scss">
@import '~styles/tools/mixins';
@import '~styles/tools/variables';

.profile-settings {
  &__logout-button {
    margin-top: 20px;

    .voxy-button__text {
      font-size: 22px;
    }
  }

  .chat-settings {
    margin-bottom: 20px;

    .voxy-checkbox__label {
      font-size: 1rem;
    }
  }

  .timezone {
    &__select {
      width: 50%;

      .multiselect__content-wrapper {
        overflow-x: hidden;
        right: 0;
        max-width: 80vw;
      }

      .multiselect__tags-wrap {
        max-width: 100%;
      }

      .multiselect__tags {
        color: $default-disable-text-grey;
        border: 1px solid $default-component-grey;
        border-radius: 0.5rem;
        background-color: $default-component-grey;
      }

      .multiselect__tag-icon:hover,
      .multiselect__tag-icon:focus {
        background: rgba(0, 0, 0, 0.1);
      }
    }

    .multiselect__input {
      color: white;
      background-color: $default-component-grey;
      font-size: 1rem;
    }

    .multiselect__content-wrapper {
      border: 0;
      border-radius: 0.5rem;
      margin-top: 0.5rem;
      color: white;
      background-color: $default-component-grey;
    }

    .multiselect__option {
      font-size: 1rem;

      &--highlight {
        color: white;
      }
    }

    .multiselect__input::placeholder {
      color: $default-disable-text-grey;
    }

    .voxy-select .multiselect--above .multiselect__content-wrapper {
      margin-top: 0;
      margin-bottom: 0.5rem;
    }

    .voxy-select .multiselect--active .multiselect__tags {
      border: 0;
      border-radius: 0.5rem;
    }

    .multiselect--active {
      border: 0;
    }

    .multiselect__single {
      font-size: 1rem;
    }
  }
}
</style>
